import { useMutation } from "@apollo/client/react";
import { Alert, App, Button, Flex, Form, Input, Space, Tabs, Typography } from "antd";
import { CheckOutlined, CreditCardOutlined, SettingOutlined, TeamOutlined, UserOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { TeamRole, UpdateUserDocument } from "../../gql/generated/graphqlSchema.tsx";
import { useCurrentSession } from "../../context/CurrentSessionContext.tsx";
import { BillingSection } from "./BillingSection.tsx";
import { LoadingBlocker } from "../../components/LoadingBlocker.tsx";
import { css } from "@linaria/core";
import { formNarrow } from "../../styles/shared.styles.ts";
import { normalizeString } from "../../utils/stringNormalize.ts";
import { MyAccountTab } from "./constants.ts";
import { TeamSection } from "./TeamSection.tsx";
import { TeamSettingsForm } from "./TeamSettingsForm.tsx";

const tabSchema = z.enum(MyAccountTab).catch(MyAccountTab.Profile);

const alertStyle = css`
  margin-bottom: var(--ant-margin);
`;

type ProfileFormValues = { name: string };

export const MyAccountPage = () => {
  const { user: currentUser, refetchUser } = useCurrentSession();
  const { message } = App.useApp();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [profileForm] = Form.useForm<ProfileFormValues>();

  const activeTab = tabSchema.parse(location.hash.replace("#", ""));

  const [updateUser, { loading: profileLoading, error: profileError }] = useMutation(UpdateUserDocument);

  const onSaveProfile = async (values: ProfileFormValues) => {
    const result = await updateUser({
      variables: {
        input: {
          name: normalizeString(values.name),
        },
      },
    });
    if (result.data?.updateUser) {
      void message.success(t("myAccount.profileUpdated"));
      profileForm.setFieldsValue({ name: result.data.updateUser.name });
      void refetchUser();
    }
  };

  const tabItems = [
    {
      key: MyAccountTab.Profile,
      icon: <UserOutlined />,
      label: t("myAccount.profileTab"),
      children: (
        <Form<ProfileFormValues>
          className={formNarrow}
          form={profileForm}
          initialValues={{ name: currentUser.name }}
          layout="vertical"
          onFinish={onSaveProfile}
          requiredMark={false}
        >
          <Form.Item
            label={t("myAccount.fullName")}
            name="name"
            rules={[{ required: true, message: t("myAccount.validation.nameRequired") }]}
          >
            <Input />
          </Form.Item>

          <Form.Item label={t("common.email")}>
            <Space vertical>
              <Input disabled value={currentUser.email} />
              <Typography.Text type="secondary">{t("myAccount.emailHelp")}</Typography.Text>
            </Space>
          </Form.Item>

          {profileError ? <Alert className={alertStyle} showIcon title={profileError.message} type="error" /> : null}

          <Form.Item>
            <Button htmlType="submit" icon={<CheckOutlined />} loading={profileLoading} type="primary">
              {t("myAccount.saveChanges")}
            </Button>
          </Form.Item>
        </Form>
      ),
    },
    {
      key: MyAccountTab.Team,
      icon: <TeamOutlined />,
      label: t("myAccount.teamTab"),
      children: <TeamSection />,
    },
    ...(currentUser.membership?.role === TeamRole.Admin
      ? [
          {
            key: MyAccountTab.Settings,
            icon: <SettingOutlined />,
            label: t("team.settings.title"),
            children: <TeamSettingsForm />,
          },
        ]
      : []),
    {
      key: MyAccountTab.Billing,
      icon: <CreditCardOutlined />,
      label: t("billing.tabTitle"),
      children: <BillingSection role={currentUser.membership?.role ?? TeamRole.Member} />,
    },
  ];

  return (
    <Flex gap="middle" vertical>
      <LoadingBlocker loading={profileLoading} />
      <Typography.Title level={3}>
        <SettingOutlined /> {t("myAccount.title")}
      </Typography.Title>

      <Tabs
        activeKey={activeTab}
        destroyOnHidden
        items={tabItems}
        onChange={(tab) => navigate({ hash: tab }, { replace: true })}
      />
    </Flex>
  );
};
